import { useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useFieldArray, useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@components/Button";
import {
  CrossIcon,
  PencilIcon,
  PlusIcon,
  ReceiptIcon,
} from "@components/icons";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { genericCustomerStorageRequest } from "@services/customerStorage/services";
import { type ICustomerStorageItemAction } from "@services/customerStorage/types";
import { queryClient } from "@/queryClient";

type TBillManagementModalType = "edit" | "create";

interface IBillManagementData {
  serviceProviderId: number;
  customerCode: string;
  serviceProviders: { serviceProviderId: number; name: string }[];
  filePlanDetails: {
    year?: number;
    month?: number;
    dueDay?: number;
    paymentAmount?: number;
    isEditable?: boolean;
  }[];
  isEditable?: boolean;
}

type TBillManagementForm = Omit<IBillManagementData, "serviceProviders">;

interface IBillManagementModelProps extends IUseModalProps {
  action: ICustomerStorageItemAction;
  parseAction: ICustomerStorageItemAction;
  variant?: TBillManagementModalType;
}

export const BillManagementModal: React.FC<IBillManagementModelProps> = ({
  action,
  parseAction,
  variant = "create",
  ...rest
}) => {
  const { t } = useTranslation();
  const modal = useModal(rest);

  const { data: parsedData, isPending: isParsingPending } =
    useQuery<IBillManagementData>({
      queryFn: () =>
        genericCustomerStorageRequest({ requestProps: parseAction }),
      queryKey: ["customerStorage", "paymentPlan", parseAction.uri],
    });

  const { mutateAsync: createPaymentPlan, isPending: isPaymentPlanPending } =
    useMutation({
      mutationFn: (data: TBillManagementForm) =>
        genericCustomerStorageRequest({
          requestProps: action,
          data,
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: ["customerStorage", "paymentPlan"],
        });

        toast.success(
          t(`customerStorage.actions.plan.${variant}.toast.success`),
        );
        modal.close();
      },
    });

  const defaultValues: TBillManagementForm = useMemo(() => {
    return {
      serviceProviderId: 0,
      customerCode: "",
      filePlanDetails: [
        {
          year: undefined,
          month: undefined,
          dueDay: undefined,
          paymentAmount: undefined,
          isEditable: true,
        },
      ],
    };
  }, []);

  const { serviceProviderOptions, formData } = useMemo(() => {
    if (!parsedData) {
      return { serviceProviderOptions: [], formData: defaultValues };
    }

    const { serviceProviders, filePlanDetails, ...rest } = parsedData;

    const serviceProviderOptions = serviceProviders
      ? serviceProviders.map((serviceProvider) => {
          return {
            value: serviceProvider.serviceProviderId,
            label: serviceProvider.name,
          };
        })
      : [];

    const formData: TBillManagementForm = {
      ...defaultValues,
      ...rest,
      filePlanDetails: filePlanDetails?.length
        ? filePlanDetails
        : defaultValues.filePlanDetails,
    };

    return { serviceProviderOptions, formData };
  }, [parsedData, defaultValues]);

  const { control, handleSubmit, reset, setError } =
    useForm<TBillManagementForm>({
      defaultValues,
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filePlanDetails",
  });

  useEffect(() => reset(formData), [reset, formData]);

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitBillManagementData: SubmitHandler<TBillManagementForm> = async (
    data,
  ) => {
    await createPaymentPlan(data).catch(setApiErrors);
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: variant === "create" ? ReceiptIcon : PencilIcon }}
      title={t(`customerStorage.actions.plan.${variant}.title`)}
      actionButtons={
        <>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t(`customerStorage.actions.plan.${variant}.buttons.cancel`)}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(submitBillManagementData)}
            isLoading={isPaymentPlanPending}
          >
            {t(`customerStorage.actions.plan.${variant}.buttons.save`)}
          </Button>
        </>
      }
    >
      <form className="flex flex-col gap-4">
        <FormAutocomplete
          name="serviceProviderId"
          control={control}
          isLoading={isParsingPending}
          options={serviceProviderOptions}
          label={t("customerStorage.form.fields.plan.serviceProviderId.label")}
          disabled={isParsingPending || !parsedData?.isEditable}
        />
        <FormInput
          control={control}
          label={t("customerStorage.form.fields.plan.customerCode.label")}
          name="customerCode"
          disabled={isParsingPending || !parsedData?.isEditable}
        />
        <div className="flex flex-col divide-y divide-grey-light rounded-xl border border-grey-light">
          {fields.map((field, index) => (
            <div key={field.id} className="flex flex-col items-start gap-2 p-2">
              <div className="flex gap-2">
                <FormInput
                  control={control}
                  label={t("customerStorage.form.fields.plan.year.label")}
                  name={`filePlanDetails.${index}.year`}
                  disabled={variant === "edit" && !field.isEditable}
                />
                <FormInput
                  control={control}
                  label={t("customerStorage.form.fields.plan.month.label")}
                  name={`filePlanDetails.${index}.month`}
                  disabled={variant === "edit" && !field.isEditable}
                />
                <FormInput
                  control={control}
                  label={t("customerStorage.form.fields.plan.dueDay.label")}
                  name={`filePlanDetails.${index}.dueDay`}
                  disabled={variant === "edit" && !field.isEditable}
                />
              </div>
              <FormInput
                control={control}
                label={t(
                  "customerStorage.form.fields.plan.paymentAmount.label",
                )}
                name={`filePlanDetails.${index}.paymentAmount`}
                disabled={variant === "edit" && !field.isEditable}
              />
              {field.isEditable && (
                <Button
                  type="button"
                  variant="secondary"
                  icon={CrossIcon}
                  onClick={() => remove(index)}
                  className="w-full"
                >
                  {t(`customerStorage.actions.plan.${variant}.buttons.remove`)}
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button
          type="button"
          onClick={() =>
            append({
              year: undefined,
              month: undefined,
              dueDay: undefined,
              paymentAmount: undefined,
              isEditable: true,
            })
          }
          variant="secondary"
          icon={PlusIcon}
          className="w-full"
        >
          {t(`customerStorage.actions.plan.${variant}.buttons.add`)}
        </Button>
      </form>
    </Modal>
  );
};
